/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties} from "react";
import {BaseComponent, Dialog_Builder, DialogModule} from "@intuitionrobotics/thunderstorm/frontend";
import {margin_tb_lg, padding_rl_lg, padding_t_med, Spacing} from "@styles/styles";
import {COLORS} from "@res/colors";
import {CardHeader} from "./CardHeader";
import {FONTS} from "@res/fonts";
import {HLine} from "./HLine";
import {OKButton} from "./OKButton";
import {OutlineButton} from "./OutlineButton";
import {HGap} from "./HGap";
import {ICONS} from "@res/icons";
import {InPlaceLoader} from "./InPlaceLoader";

const dialogCardStyle: React.CSSProperties = {
    minWidth: 500,
    maxWidth: 800,
    minHeight: 50,
    margin: Spacing.xl,
    borderRadius: 4,
    backgroundColor: COLORS.white,
    boxShadow: `0 4px 16px 0 rgba(0, 0, 0, 0.05)`
};

export const showDialog = (dialog: React.ReactNode) => {
    new Dialog_Builder(dialog)
        .clearInlineStyle()
        .setStyle(dialogCardStyle)
        .setOverlayColor(`rgb(42, 49, 56, 0.54)`) // COLORS.neutral_text_dark as rgb
        .setAllowIndirectClosing(false)
        .show();
};

type DialogCardProps = {
    title?: string
    showX?: boolean
    loading?: boolean
    saveLabel?: string
    onSave?: () => boolean | Promise<boolean> | void // return true to handle dialog closing yourself
    cancelLabel?: string
    onCancel?: () => boolean | void // return true to handle dialog closing yourself
    onClose?: () => boolean | void // return true to handle dialog closing yourself
    content?: React.ReactNode
    footerExtra?: React.ReactNode
    style?: CSSProperties
    saveEnabled?: boolean
    cancelEnabled?: boolean
}

type DialogCardState = {}

export class DialogCard
    extends BaseComponent<DialogCardProps, DialogCardState> {

    static defaultProps = {
        cancelEnabled: true
    };

    constructor(props: DialogCardProps) {
        super(props);
    }

    private handleDialogClose = () => {
        if (this.props.onClose === undefined || !this.props.onClose()) {
            return DialogModule.close();
        }
    };

    private handleOnCancel = () => {
        if (this.props.onCancel === undefined)
            return this.handleDialogClose();

        if (!this.props.onCancel()) {
            return DialogModule.close();
        }
    };

    private handleOnSave = () => {
        this.setState(
            {
                saving: true
            },
            () => {
                if (this.props.onSave === undefined) {
                    return DialogModule.close();
                }
                Promise.resolve(this.props.onSave()).then((result => {
                    if (!result) {
                        return DialogModule.close();
                    }
                })).catch(() => DialogModule.close());
            }
        );
    };

    private renderXButton = () => {
        if (!this.props.showX)
            return null;

        const xSize = 9;
        return <div className={'x-btn-wrapper'}>
            <div className={'clickable'}
                 style={{padding: Spacing.s, width: xSize, lineHeight: '0px'}}
                 onClick={() => this.handleDialogClose()}
            >
                {ICONS.close(COLORS.neutral_gray_1, xSize)}
            </div>
        </div>;
    };

    private renderHeader = () => {
        if (!this.props.title)
            return null;

        return <>
            <CardHeader>
                {FONTS.medium_18_blue(this.props.title)}
            </CardHeader>
            <HLine style={{marginTop: Spacing.s}}/>
        </>;
    };

    private renderFooterExtra = () => {
        return <div style={{flexGrow: 1}}>
            {this.props.footerExtra}
        </div>;
    };

    private renderSaveCancel = () => {
        const saveEnabled = this.props.saveEnabled !== undefined ? this.props.saveEnabled : !this.props.loading;
        return <div className={'ll_h_c'}>
            <HGap h={Spacing.l}/>
            <OutlineButton
                text={this.props.cancelLabel || 'Cancel'}
                textStyle={{whiteSpace: 'nowrap'}}
                enabled={this.props.cancelEnabled}
                onClick={() => this.handleOnCancel()}
            />
            <HGap h={Spacing.m}/>
            <OKButton
                text={this.props.saveLabel || 'Save'}
                textStyle={{whiteSpace: 'nowrap'}}
                enabled={saveEnabled}
                onClick={() => saveEnabled && this.handleOnSave()}
            />
        </div>;
    };

    private renderFooter = () => {
        return <div className={`ll_h_c ${padding_rl_lg} ${margin_tb_lg}`}>
            {this.renderFooterExtra()}
            {this.renderSaveCancel()}
        </div>;
    };

    renderBody = () => {
        return <div className={`${padding_rl_lg} ${padding_t_med}`} style={{display: 'flex', flex: 1, maxHeight: '70vh'}}>
            <div className={'ll_v_s'} style={{flexGrow: 1, minHeight: 'min-content', position: 'relative'}}>
                {this.props.content}
            </div>
        </div>;
    };

    render() {
        return <div className={`ll_v_s`} style={{position: 'relative', ...this.props.style}}>
            <InPlaceLoader loading={this.props.loading}/>
            {this.renderXButton()}
            {this.renderHeader()}
            {this.renderBody()}
            {this.renderFooter()}
        </div>;
    }

}
